import { useMemo } from "react";
import { useAuth } from "../../context/AuthContext";

export interface HotelPermissions {
  canViewModule: boolean;
  canViewReservations: boolean;
  canViewRooms: boolean;
  canViewRates: boolean;
  canViewGuests: boolean;
  canViewAccounts: boolean;
  canViewFolios: boolean;
  canViewServices: boolean;
  canViewOperations: boolean;
  canViewPayments: boolean;
  canViewExpenses: boolean;
  canViewConfiguration: boolean;
}

export const useHotelPermissions = (): HotelPermissions => {
  const auth = useAuth();

  return useMemo<HotelPermissions>(() => {
    if (!auth) {
      return {
        canViewModule: false,
        canViewReservations: false,
        canViewRooms: false,
        canViewRates: false,
        canViewGuests: false,
        canViewAccounts: false,
        canViewFolios: false,
        canViewServices: false,
        canViewOperations: false,
        canViewPayments: false,
        canViewExpenses: false,
        canViewConfiguration: false,
      };
    }

    if (auth.user?.is_superadmin || auth.user?.role === "SysAdmin") {
      return {
        canViewModule: true,
        canViewReservations: true,
        canViewRooms: true,
        canViewRates: true,
        canViewGuests: true,
        canViewAccounts: true,
        canViewFolios: true,
        canViewServices: true,
        canViewOperations: true,
        canViewPayments: true,
        canViewExpenses: true,
        canViewConfiguration: true,
      };
    }

    const hasPermission = (key: string): boolean => {
      return auth.hasPermission?.(key) || false;
    };

    const canViewReservations = hasPermission("Hotellerie.Reservations.view");
    const canViewRooms = hasPermission("Hotellerie.Configuration.Chambres.view");
    const canViewRates =
      hasPermission("Hotellerie.Tarifs.PlansTarifaires.view") ||
      hasPermission("Hotellerie.Tarifs.Calendrier.view");
    const canViewGuests = hasPermission("Hotellerie.Clients.view");
    const canViewAccounts = hasPermission("Hotellerie.Comptes.view");
    const canViewFolios = hasPermission("Hotellerie.Folios.view");
    const canViewServices = hasPermission("Hotellerie.Services.view");
    const canViewOperations =
      hasPermission("Hotellerie.Operations.Housekeeping.view") ||
      hasPermission("Hotellerie.Operations.Maintenance.view");
    const canViewPayments = hasPermission("Hotellerie.Paiements.view");
    const canViewExpenses =
      hasPermission("Hotellerie.Depenses.Categories.view") ||
      hasPermission("Hotellerie.Depenses.SaisieDepenses.view");
    const canViewConfiguration =
      hasPermission("Hotellerie.Configuration.Proprietes.view") ||
      hasPermission("Hotellerie.Configuration.Batiments.view") ||
      hasPermission("Hotellerie.Configuration.Etages.view") ||
      hasPermission("Hotellerie.Configuration.TypesChambres.view") ||
      hasPermission("Hotellerie.Configuration.Amenites.view");

    const canViewModule =
      canViewReservations ||
      canViewRooms ||
      canViewRates ||
      canViewGuests ||
      canViewAccounts ||
      canViewFolios ||
      canViewServices ||
      canViewOperations ||
      canViewPayments ||
      canViewExpenses ||
      canViewConfiguration;

    return {
      canViewModule,
      canViewReservations,
      canViewRooms,
      canViewRates,
      canViewGuests,
      canViewAccounts,
      canViewFolios,
      canViewServices,
      canViewOperations,
      canViewPayments,
      canViewExpenses,
      canViewConfiguration,
    };
  }, [auth]);
};
